import { useState } from "react";
import { useSession } from "../session/useSession";
import { ui, disabledStyle } from "../components/uiStyles";

/**
 * Paso 3: Opciones concretas con checkboxes para comparar después.
 *
 * Backend devuelve TODO en toolOutput (forced tool_choice — no hay markdown).
 * Esquema:
 *   summary: string             ← intro general
 *   options: [
 *     {
 *       id, name, price,
 *       specs: string[],        ← specs principales
 *       pros: string[],         ← ventajas
 *       cons: string[],         ← limitaciones
 *       is_alternative: bool,
 *       is_best_value: bool,
 *     }
 *   ]
 */
export function Step3Options() {
  const {
    formData,
    responses,
    loading,
    error,
    runOptions,
    updateFormField,
    resetStep,
    goToStep,
  } = useSession();

  const [selected, setSelected] = useState([]);

  const r3 = responses[3];
  const tool = r3?.toolOutput;
  const hasResult = !!tool;
  const summary = tool?.summary || "";
  const options = tool?.options || [];

  // Handlers — actualizan formData directo (persisten al navegar)
  const setRequirements = (v) => updateFormField("requirements", v);
  const setBudget = (v) => updateFormField("budget", v);
  const setExtraContext = (v) => updateFormField("extraContext", v);

  const toggle = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const handleSearch = () => {
    runOptions(
      formData.requirements || "",
      formData.budget || "",
      formData.extraContext || ""
    );
  };

  const handleResearch = () => {
    setSelected([]);
    resetStep(3);
  };

  const handleCompare = () => {
    const names = options
      .filter((o) => selected.includes(o.id))
      .map((o) => o.name)
      .join(", ");
    updateFormField("selectedOptionNames", names);
    resetStep(4);
    resetStep(5);
    goToStep(4);
  };

  return (
    <div style={ui.card}>
      <h2 style={ui.h2}>
        Opciones para <em style={{ color: "#6c63ff" }}>{formData.product}</em>
      </h2>

      {/* Formulario: visible si no hay resultado */}
      {!hasResult && (
        <>
          <div style={{ marginBottom: "14px" }}>
            <label style={ui.label}>Requisitos mínimos</label>
            <textarea
              style={ui.textarea}
              placeholder="ej. mínimo 16GB RAM, batería más de 8 horas..."
              value={formData.requirements || ""}
              onChange={(e) => setRequirements(e.target.value)}
            />
          </div>
          <div style={{ marginBottom: "14px" }}>
            <label style={ui.label}>Rango de presupuesto</label>
            <input
              style={ui.input}
              type="text"
              placeholder="ej. $10,000 - $15,000 MXN"
              value={formData.budget || ""}
              onChange={(e) => setBudget(e.target.value)}
            />
          </div>
          <div style={{ marginBottom: "16px" }}>
            <label style={ui.label}>Contexto adicional (opcional)</label>
            <textarea
              style={ui.textarea}
              placeholder="ej. prefiero entrega rápida, lo necesito antes de fin de mes..."
              value={formData.extraContext || ""}
              onChange={(e) => setExtraContext(e.target.value)}
            />
          </div>
          <button
            style={{
              ...ui.primaryBtn,
              ...((!(formData.requirements || "").trim() &&
                !(formData.budget || "").trim()) ||
              loading
                ? disabledStyle
                : {}),
            }}
            disabled={
              (!(formData.requirements || "").trim() &&
                !(formData.budget || "").trim()) ||
              loading
            }
            onClick={handleSearch}
          >
            {loading ? "Buscando..." : "Encontrar opciones →"}
          </button>
        </>
      )}

      {error && <div style={ui.errorBox}>❌ {error}</div>}

      {/* Resultado */}
      {hasResult && (
        <>
          {/* Summary general */}
          {summary && (
            <p style={{ fontSize: "14px", color: "#555", marginBottom: "16px" }}>
              {summary}
            </p>
          )}

          {options.length > 0 ? (
            <div style={{ marginTop: "8px" }}>
              <label style={ui.label}>
                Selecciona las opciones que quieras comparar
              </label>
              {options.map((opt) => (
                <div
                  key={opt.id}
                  style={ui.optionCard(selected.includes(opt.id))}
                  onClick={() => toggle(opt.id)}
                >
                  <div style={ui.checkbox(selected.includes(opt.id))}>
                    {selected.includes(opt.id) ? "✓" : ""}
                  </div>
                  <div style={{ flex: 1 }}>
                    {/* Header de la opción: nombre + badges */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        flexWrap: "wrap",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: "15px",
                          color: "#1a1a2e",
                        }}
                      >
                        {opt.name}
                      </div>
                      {opt.is_best_value && (
                        <span
                          style={{
                            background: "#fff3cd",
                            color: "#8a6000",
                            fontSize: "11px",
                            padding: "2px 8px",
                            borderRadius: "10px",
                            fontWeight: 600,
                          }}
                        >
                          ⭐ Mejor relación calidad-precio
                        </span>
                      )}
                      {opt.is_alternative && (
                        <span
                          style={{
                            fontSize: "11px",
                            color: "#999",
                            fontWeight: 400,
                          }}
                        >
                          (alternativa)
                        </span>
                      )}
                    </div>

                    {/* Precio */}
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#1a4a7a",
                        marginTop: "4px",
                        fontWeight: 500,
                      }}
                    >
                      {opt.price}
                    </div>

                    {/* Specs principales */}
                    {Array.isArray(opt.specs) && opt.specs.length > 0 && (
                      <ul
                        style={{
                          fontSize: "13px",
                          color: "#444",
                          margin: "8px 0 0",
                          paddingLeft: "18px",
                        }}
                      >
                        {opt.specs.map((s, i) => (
                          <li key={i}>{s}</li>
                        ))}
                      </ul>
                    )}

                    {/* Pros */}
                    {Array.isArray(opt.pros) && opt.pros.length > 0 && (
                      <div style={{ marginTop: "8px" }}>
                        {opt.pros.map((p, i) => (
                          <div
                            key={i}
                            style={{
                              fontSize: "12px",
                              color: "#1a6b3a",
                              marginTop: "2px",
                            }}
                          >
                            ✅ {p}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Cons */}
                    {Array.isArray(opt.cons) && opt.cons.length > 0 && (
                      <div style={{ marginTop: "6px" }}>
                        {opt.cons.map((c, i) => (
                          <div
                            key={i}
                            style={{
                              fontSize: "12px",
                              color: "#8a6000",
                              marginTop: "2px",
                            }}
                          >
                            ⚠️ {c}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={ui.errorBox}>
              ⚠ El modelo no devolvió opciones. Intenta buscar de nuevo o
              modifica los requisitos.
            </div>
          )}

          <div
            style={{
              display: "flex",
              gap: "10px",
              marginTop: "16px",
              flexWrap: "wrap",
            }}
          >
            {selected.length >= 1 && (
              <button style={ui.primaryBtn} onClick={handleCompare}>
                Comparar seleccionadas ({selected.length}) →
              </button>
            )}
            <button style={ui.secondaryBtn} onClick={handleResearch}>
              🔄 Buscar de nuevo
            </button>
            <button style={ui.secondaryBtn} onClick={() => goToStep(2)}>
              ← Volver
            </button>
          </div>
        </>
      )}
    </div>
  );
}
